// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Benchmark structure representing a portfolio allocation. Each benchmark is an
 * array of tickers with their corresponding weights.
 */
export type RiskProfileBenchmark = Array<{ ticker: string; weight: number }>;

/**
 * Maps each risk profile type to its corresponding benchmark.
 *
 * Each benchmark is represented as an array of tickers with their weights. Weights
 * are expressed as decimals (e.g., 0.20 for 20%).
 *
 * @example
 *
 * ```ts
 * const benchmark = RISK_PROFILE_BENCHMARK_MAP.get('conservative');
 * // Returns: [{ ticker: 'ES0183091000', weight: 0.20 }, ...]
 * ```
 */
export const RISK_PROFILE_BENCHMARK_MAP = new Map<string, RiskProfileBenchmark>([
  [
    'conservative',
    [
      { ticker: 'ES0183091000', weight: 0.2 },
      { ticker: 'FR0013346079', weight: 0.2 },
      { ticker: 'LU2008763182', weight: 0.15 },
      { ticker: 'LU1170327958', weight: 0.15 },
      { ticker: 'LU2188668169', weight: 0.125 },
      { ticker: 'LU0239680886', weight: 0.125 },
      { ticker: 'IE00B86Y3465', weight: 0.025 },
      { ticker: 'IE00BF2ZV712', weight: 0.025 },
    ],
  ],
  [
    'moderate',
    [
      { ticker: 'ES0183091000', weight: 0.1 },
      { ticker: 'FR0013346079', weight: 0.1 },
      { ticker: 'LU2008763182', weight: 0.1 },
      { ticker: 'LU1170327958', weight: 0.1 },
      { ticker: 'LU2188668169', weight: 0.1 },
      { ticker: 'LU0239680886', weight: 0.1 },
      { ticker: 'LU0616839501', weight: 0.025 },
      { ticker: 'LU0086177085', weight: 0.025 },
      { ticker: 'IE00B86Y3465', weight: 0.025 },
      { ticker: 'IE00BF2ZV712', weight: 0.025 },
      { ticker: 'LU0127786431', weight: 0.075 },
      { ticker: 'LU0210529490', weight: 0.075 },
      { ticker: 'LU0218911690', weight: 0.038 },
      { ticker: 'LU1880398125', weight: 0.037 },
      { ticker: 'LU0157217158', weight: 0.038 },
      { ticker: 'LU1295552621', weight: 0.037 },
    ],
  ],
  [
    'dynamic',
    [
      { ticker: 'ES0183091000', weight: 0.05 },
      { ticker: 'FR0013346079', weight: 0.05 },
      { ticker: 'LU2008763182', weight: 0.05 },
      { ticker: 'LU1170327958', weight: 0.05 },
      { ticker: 'LU2188668169', weight: 0.05 },
      { ticker: 'LU0239680886', weight: 0.05 },
      { ticker: 'LU0616839501', weight: 0.025 },
      { ticker: 'LU0086177085', weight: 0.025 },
      { ticker: 'IE00B86Y3465', weight: 0.025 },
      { ticker: 'IE00BF2ZV712', weight: 0.025 },
      { ticker: 'LU0127786431', weight: 0.15 },
      { ticker: 'LU0210529490', weight: 0.15 },
      { ticker: 'LU0218911690', weight: 0.075 },
      { ticker: 'LU1880398125', weight: 0.075 },
      { ticker: 'LU0157217158', weight: 0.06 },
      { ticker: 'LU1295552621', weight: 0.06 },
      { ticker: 'LU1543584988', weight: 0.01 },
      { ticker: 'LU0616900774', weight: 0.01 },
      { ticker: 'LU1176912761', weight: 0.01 },
    ],
  ],
  [
    'aggressive',
    [
      { ticker: 'LU2188668169', weight: 0.025 },
      { ticker: 'LU0239680886', weight: 0.025 },
      { ticker: 'IE00B86Y3465', weight: 0.025 },
      { ticker: 'IE00BF2ZV712', weight: 0.025 },
      { ticker: 'LU0127786431', weight: 0.225 },
      { ticker: 'LU0210529490', weight: 0.225 },
      { ticker: 'LU0218911690', weight: 0.113 },
      { ticker: 'LU1880398125', weight: 0.112 },
      { ticker: 'LU0157217158', weight: 0.09 },
      { ticker: 'LU1295552621', weight: 0.09 },
      { ticker: 'LU1543584988', weight: 0.015 },
      { ticker: 'LU0616900774', weight: 0.015 },
      { ticker: 'LU1176912761', weight: 0.015 },
    ],
  ],
]);

/**
 * Gets the benchmark for a given risk profile type.
 *
 * @param riskProfile - The risk profile type (e.g., 'conservative', 'moderate',
 *   'dynamic', 'aggressive')
 * @returns The benchmark array for the given risk profile, or the moderate profile
 *   benchmark if the profile is not found
 */
export const getBenchmarkByRiskProfile = (
  riskProfile: string | null | undefined,
): RiskProfileBenchmark => {
  const normalizedProfile = riskProfile?.toLowerCase() ?? '';

  if (RISK_PROFILE_BENCHMARK_MAP.has(normalizedProfile)) {
    return RISK_PROFILE_BENCHMARK_MAP.get(normalizedProfile)!;
  }

  return [];
};
